/**
 * Level flow (spec 02 §6, §9) - owned by the integrator: the fly-in, the level timer and HURRY UP,
 * the Baron's arrival, the level clear with its bonus, the rounds that end early (EXTEND, doors,
 * umbrellas, the flood, the bonus stage, the win), and the freezes. Runs once per frame, after the
 * systems, so it sees this frame's deaths.
 */

import { levelClearBonus } from './bubbles';
import { effectsLook } from './items';
import { flyInFrame, flyInFrames } from './flyIn';
import { angerAll, spawnBarons } from './monsters';
import { isSettled } from './players';
import {
  BARON_SECONDS, CLEAR_FRAMES, DROP_IN_FREEZE_FRAMES, EXTEND_FRAMES, FRAME_HZ, HURRY_FREEZE_FRAMES, hurrySeconds,
  WON_FRAMES,
} from './tuning';
import type { FlowState, LevelExit, World } from './types';

export { flyInFrame } from './flyIn';

export function initialFlow(levelNumber: number): FlowState {
  return {
    phase: 'play', exit: { kind: 'next' }, endFrames: 0, frame: 0, timer: hurrySeconds(levelNumber) ?? -1,
    secondFrames: FRAME_HZ, hurry: false, baron: false, clearFrames: 0, freeze: DROP_IN_FREEZE_FRAMES,
    extendFrames: 0, flyFrom: [{ x: 0, y: 0 }, { x: 0, y: 0 }], flyTo: [{ x: 0, y: 0 }, { x: 0, y: 0 }], flyFrame: 0,
  };
}

/** A monster still in the level (not yet killed): the level is clear when none remain. */
const alive = (world: World): boolean => world.monsters.some((m) => m.state !== 'dead');

/**
 * Per-second timer: HURRY UP at 0, then the Baron 10 s later (02 §6). Stops once the Baron is out,
 * and pauses under the clock (02 §7.5).
 */
function tickTimer(world: World): void {
  const f = world.flow;
  if (f.timer < 0 || f.phase !== 'play' || world.power.clock > 0) return;
  if (--f.secondFrames > 0) return;
  f.secondFrames = FRAME_HZ;
  if (--f.timer > 0) return;
  if (!f.hurry) {
    f.hurry = true;
    f.timer = BARON_SECONDS;
    f.freeze = HURRY_FREEZE_FRAMES;
    angerAll(world);
    world.events.push({ type: 'hurryUp' });
    return;
  }
  f.baron = true;
  f.timer = -1;
  spawnBarons(world);
  world.events.push({ type: 'baron' });
}

/**
 * Every Baron has gone (it killed, its player died, a yellow cross): the C64 restarts the per-second
 * timer ($1479), which it reloaded to the level's full hurry time when the Baron came ($1681-$168F),
 * so HURRY UP and the Baron can come round again.
 */
function baronsGone(world: World): void {
  const f = world.flow;
  if (!f.baron || world.barons.length > 0 || f.phase !== 'play') return;
  const s = hurrySeconds(world.level.n);
  f.baron = false;
  f.hurry = false;
  f.timer = s ?? -1;
  f.secondFrames = FRAME_HZ;
}

/** The fly-in: the players glide to their starts, then the level begins with the drop-in's freeze. */
function flyIn(world: World): void {
  const f = world.flow;
  const next = f.flyFrame + 1;
  flyInFrame(world, next);
  if (next < flyInFrames(world)) return;
  f.phase = 'play';
  f.frame = 0;
  f.freeze = DROP_IN_FREEZE_FRAMES;
  world.events.push({ type: 'flyInDone' });
}

/**
 * The last monster is gone (02 §7.6): the Barons leave, and the level's bonus - bubble food on the
 * scheduled levels (1, 5, 10, 16 ...: the gap grows by one each time, and a skipped one breaks the
 * schedule, as on the C64), the giant item after a staff or box.
 */
function levelCleared(world: World): void {
  const f = world.flow;
  const c = world.carry;
  f.phase = 'clear';
  f.clearFrames = CLEAR_FRAMES;
  world.barons.length = 0;
  const bubbleFood = world.level.n === c.bubbleFoodAt;
  if (bubbleFood) {
    c.bubbleFoodAt += c.bubbleFoodGap;
    c.bubbleFoodGap++;
  }
  world.events.push({ type: 'levelClear' });
  if (bubbleFood || world.power.giant >= 0) levelClearBonus(world, { bubbleFood, giant: world.power.giant });
}

function finish(world: World): void {
  world.flow.phase = 'done';
  world.events.push({ type: 'levelDone' });
}

export function updateFlow(world: World): void {
  const f = world.flow;
  if (f.phase === 'flyIn') return flyIn(world);
  if (f.phase === 'done') return;
  f.frame++;
  if (f.freeze > 0) f.freeze--;
  for (const e of world.events) if (e.type === 'won') endRound(world, { kind: 'ending' }, WON_FRAMES);
  if (f.endFrames > 0) {
    if (--f.endFrames === 0) finish(world);
    return;
  }
  baronsGone(world);
  if (f.extendFrames > 0) {
    if (--f.extendFrames === 0) finish(world);
    return;
  }
  tickTimer(world);
  // The C64 cannot clear a level while the clock runs (D_1578 skips $16F7) nor during a blast (its
  // monster count is held at 1 until $2A3F).
  const held = world.power.clock > 0 || effectsLook(world).blast !== 'none';
  if (f.phase === 'play' && world.monsters.length > 0 && !alive(world) && !held) {
    levelCleared(world);
  } else if (f.phase === 'clear' && --f.clearFrames <= 0) {
    const settled = world.players.every((p) => p.state !== 'play' || isSettled(world, p.id));
    if (settled) finish(world);
  }
}

/**
 * A player died (02 §3 Respawn): the HURRY UP countdown starts again, unless the Baron is out or
 * the level is clear. The PLAYER stream calls this from `killPlayer`.
 */
export function resetHurry(world: World): void {
  const f = world.flow;
  if (f.baron || f.phase !== 'play') return;
  const s = hurrySeconds(world.level.n);
  if (s === null) return;
  f.hurry = false;
  f.timer = s;
  f.secondFrames = FRAME_HZ;
}

/**
 * A player spelt EXTEND (02 §7.7): the BUBBLE stream calls this once it has paid the life. The
 * round ends after the fanfare (about 8 s), its items and bonus lost; nothing else runs meanwhile.
 */
export function extendRound(world: World): void {
  const f = world.flow;
  f.extendFrames = EXTEND_FRAMES;
  f.freeze = EXTEND_FRAMES;
  world.barons.length = 0;
}

/**
 * End the round early and go to `exit` in `frames` frames (at least 1): a door (at once), an umbrella,
 * the blue cross's flood, the potion bonus stage, level 100 won. The caller decides what else stops
 * meanwhile (a freeze, its own animation); the round's clear bonus is not paid. Any stream may call it.
 */
export function endRound(world: World, exit: LevelExit, frames = 1): void {
  const f = world.flow;
  if (f.endFrames > 0 || f.phase === 'done') return;
  f.exit = exit;
  f.endFrames = Math.max(1, frames);
  world.barons.length = 0;
}
